<script setup lang="ts">
import { watchEffect, onScopeDispose } from "vue";
import { useWorkspace } from "@/useWorkspace";

const { workspaceRole } = useWorkspace();

// Toggle a class on <body> so the rest of the page reserves space for
// the banner. Done from JS rather than a global stylesheet so the
// reservation only applies when the banner is actually rendered.
const BODY_CLASS = "has-super-admin-banner";
watchEffect(() => {
  const active = workspaceRole.value === "SuperAdminViewer";
  document.body.classList.toggle(BODY_CLASS, active);
});
onScopeDispose(() => {
  document.body.classList.remove(BODY_CLASS);
});
</script>

<template>
  <div
    v-if="workspaceRole === 'SuperAdminViewer'"
    data-testid="super-admin-banner"
    class="super-admin-banner">
    <span class="dot">●</span>
    Viewing as super admin — not a member of this workspace. Writes will fail.
  </div>
</template>

<style>
/* Global: when the banner is active, reserve 24px at the top of body so
   the page content does not sit under the banner. */
body.has-super-admin-banner {
  padding-top: 24px;
  box-sizing: border-box;
}
/* Fixed-positioned top-0 elements (e.g. TopBar) don't participate in
   body padding, so push them down by the banner height too. */
body.has-super-admin-banner .fixed.top-0 {
  top: 24px !important;
}
</style>

<style scoped>
.super-admin-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 24px;
  padding: 0 12px;
  background: #fef2f2;
  color: #b91c1c;
  border-bottom: 1px solid #b91c1c;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  pointer-events: none;
  user-select: none;
}
.dot {
  font-size: 13px;
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
